/** Snapchat-only conversion guards; never changes entitlement decisions. */
export const SNAP_TRIAL_MS = 3 * 24 * 60 * 60 * 1000;

export function isSnapThreeDayTrial(start: number, end: number): boolean {
  return Number.isFinite(start) && Number.isFinite(end) && end - start === SNAP_TRIAL_MS;
}

export function snapConsent(platform: "ios" | "android", optedOut: boolean, native?: {
  attStatus?: string; advertisingConsent?: string;
}): boolean {
  if (optedOut || native?.advertisingConsent !== "granted") return false;
  return platform !== "ios" || native.attStatus === "authorized";
}

export function snapStoreConversion(record: {
  platform: string; environment: string; status: string; isTrial: boolean;
  startedAt: string | null; expiresAt: string | null;
  transactionRef: string; raw: Record<string, unknown>;
}, eventType: string): {
  eventName: "START_TRIAL" | "PURCHASE"; transactionId: string;
  eventTimeMs: number; value?: number; currency?: string;
} | null {
  if (record.environment.toLowerCase() !== "production") return null;
  const tx = record.platform === "apple" && record.raw.transaction && typeof record.raw.transaction === "object"
    ? record.raw.transaction as Record<string, unknown> : record.raw;
  const start = record.platform === "apple" && typeof tx.purchaseDate === "number" ? tx.purchaseDate : record.startedAt ? Date.parse(record.startedAt) : NaN;
  const end = record.platform === "apple" && typeof tx.expiresDate === "number" ? tx.expiresDate : record.expiresAt ? Date.parse(record.expiresAt) : NaN;
  // Google offer IDs and Apple offerType alone do not prove a free trial.
  const freeTrial = record.platform === "apple"
    ? tx.offerDiscountType === "FREE_TRIAL"
    : Boolean((record.raw.lineItems as Array<{ offerPhase?: { freeTrial?: unknown } }> | undefined)?.[0]?.offerPhase?.freeTrial);
  if (record.isTrial && record.status === "trialing" && freeTrial &&
    ["trial_started", "started", "updated"].includes(eventType) && isSnapThreeDayTrial(start, end)) {
    return { eventName: "START_TRIAL", transactionId: record.transactionRef, eventTimeMs: start };
  }
  if (record.status !== "active" || record.isTrial || !["started", "renewed"].includes(eventType)) return null;
  // Apple's signed transaction price is milliunits of currency. Subscription
  // catalog/recurring prices are NOT proof of a Google charge: omit until the
  // transaction-specific Orders API is connected.
  if (record.platform !== "apple" || tx.revocationDate) return null;
  const { price, currency, transactionId, purchaseDate } = tx;
  if (typeof price !== "number" || !Number.isFinite(price) || price <= 0 ||
    typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency) ||
    typeof transactionId !== "string" || !transactionId ||
    typeof purchaseDate !== "number" || !Number.isFinite(purchaseDate)) return null;
  return { eventName: "PURCHASE", transactionId, eventTimeMs: purchaseDate, value: price / 1000, currency };
}